import type {
    GenerationBasisData,
    GenerationBasisInput,
    GenerationBasisItem,
    GenerationBasisSource,
    GenerationBasisView,
    GenerationBasisViewItem,
} from './types'

export const BASIS_CHANGED = 'BASIS_CHANGED'
export const BASIS_UNAVAILABLE = 'GENERATION_BASIS_UNAVAILABLE'
export const BASIS_ITEM_UNKNOWN = 'GENERATION_BASIS_ITEM_UNKNOWN'

/** Signature of a generation made from no item at all, whether the user skipped the insight or none was used. */
export const EMPTY_BASIS_SIGNATURE = 'none'

/** What the panel calls the sources it shows, unless a screen passes its own wording. */
export const DEFAULT_BASIS_NOUN = 'audience insight'

export const basisChangedMessage = (noun: string = DEFAULT_BASIS_NOUN) =>
    `Something changed in the ${noun} since you reviewed it. Review it again, then generate again.`
export const basisUnavailableMessage = (noun: string = DEFAULT_BASIS_NOUN) =>
    `The ${noun} could not be read right now. Please try again.`
export const BASIS_ACTION_FAILED_MESSAGE = 'We could not save that change. Please try again.'
export const BASIS_ITEM_UNKNOWN_MESSAGE = 'That item is no longer offered. The list was reloaded.'

const SOURCE_LABELS: Record<string, string> = {
    insight: 'Audience insight',
    ad_history: 'Past ads',
}

const KIND_LABELS: Record<string, string> = {
    interest: 'Interest',
    behaviour: 'Behaviour',
    category: 'Category',
    theme: 'Theme',
    tone: 'Audience tone',
    winning_ad: 'Winning ad',
}

const lower = (label: string) => label.toLowerCase()

const reasonNote = (reason: string | null, label: string): string | null => {
    switch (reason) {
        case 'unknown_age':
            return `We cannot tell how recent the ${lower(label)} data is.`
        case 'no_insight_fields':
            return `There is not enough ${lower(label)} data for this campaign yet.`
        case 'special_ad_category':
            return `Nothing from ${lower(label)} is applied to special ad categories.`
        case 'decisions_unavailable':
            return 'Your choices could not be read, so nothing was used.'
        default:
            return null
    }
}

const humanise = (value: string) => {
    const spaced = value.replace(/[_-]+/g, ' ').trim()
    return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : value
}

export const sourceLabelOf = (source: string) => SOURCE_LABELS[source] ?? humanise(source)

export const kindLabelOf = (kind: string) => KIND_LABELS[kind] ?? humanise(kind)

/** Plain sentence for the state of a source, or null when nothing needs saying. */
export const sourceStateNote = (state: string, reason: string | null, source: string): string | null => {
    const label = sourceLabelOf(source)
    if (state === 'insufficient')
        return reasonNote(reason, label) ?? `There is not enough ${lower(label)} data to use yet.`
    if (state === 'stale') return `The ${lower(label)} data is out of date, so it is not used automatically.`
    if (state === 'unavailable') return `The ${lower(label)} data is not available right now.`
    return null
}

/** Plain sentence for why a stored generation applied no item of a source, or null. */
export const notAppliedNote = (notApplied: string | null, source: string): string | null => {
    if (!notApplied) return null
    if (notApplied === 'skipped') return 'You chose to generate without insight.'
    const label = sourceLabelOf(source)
    return reasonNote(notApplied, label) ?? `Nothing from ${lower(label)} was applied.`
}

interface DisplayedItem {
    kind: string
    text: string | null
    fields: Record<string, unknown> | null
}

const textField = (fields: Record<string, unknown> | null, name: string) => {
    const value = fields?.[name]
    return typeof value === 'string' && value.trim() ? value.trim() : null
}

/** The lines to show for an item: the fields of a winning ad when it has them, else its text. */
export const itemLinesOf = ({ text, fields }: DisplayedItem): string[] => {
    const headline = textField(fields, 'headline')
    const primary = textField(fields, 'primary_text')
    if (headline || primary) {
        return [headline ? `Headline: ${headline}` : null, primary ? `Text: ${primary}` : null].filter(
            (line): line is string => line !== null,
        )
    }
    return text ? [text] : []
}

/** Whether any source is switched on, so that a generation is shaped by (and must send) the basis. */
export const isBasisActive = (basis: GenerationBasisData | null | undefined): basis is GenerationBasisData =>
    !!basis && basis.sources.some((source) => source.mode !== 'off')

export const activeSourcesOf = (basis: GenerationBasisData): GenerationBasisSource[] =>
    basis.sources.filter((source) => source.mode !== 'off')

/**
 * The `basis` argument for a generation request: a skip whenever the user's choice to generate without insight is on
 * (also when every source reads off, so a skip is never dropped), else the displayed hash, or undefined while the
 * feature is off or the basis is not known.
 */
export const basisRequestOf = (
    basis: GenerationBasisData | null | undefined,
    skip: boolean,
): GenerationBasisInput | undefined => {
    if (!basis) return undefined
    if (skip) return { skip: true }
    if (!isBasisActive(basis)) return undefined
    return basis.basisHash ? { basisHash: basis.basisHash } : undefined
}

/**
 * What a generation made now would be based on, for judging whether a stored result is out of date. A skipped
 * generation and one that used no item are the same (nothing was supplied); null while the feature is off and
 * undefined while it is not known yet.
 */
export const basisSignatureOf = (
    basis: GenerationBasisData | null | undefined,
    skip: boolean,
): string | null | undefined => {
    if (basis === undefined) return undefined
    if (skip && basis) return EMPTY_BASIS_SIGNATURE
    if (!isBasisActive(basis)) return null
    const used = basis.items.some((item: GenerationBasisItem) => item.state === 'used')
    return used && basis.basisHash ? basis.basisHash : EMPTY_BASIS_SIGNATURE
}

/** The code of the first GraphQL error of a failed request. */
export const errorCodeOf = (error: unknown): string | null => {
    const graphQLErrors = (error as { graphQLErrors?: { extensions?: { code?: string } }[] })?.graphQLErrors
    return graphQLErrors?.[0]?.extensions?.code ?? null
}

/** Message for an error of the basis, or null when it is not one. */
export const basisErrorMessageOf = (error: unknown, noun: string = DEFAULT_BASIS_NOUN): string | null => {
    const code = errorCodeOf(error)
    if (code === BASIS_CHANGED) return basisChangedMessage(noun)
    if (code === BASIS_UNAVAILABLE) return basisUnavailableMessage(noun)
    return null
}

/** The used items of a stored view, the ones the model says shaped the result first. */
export const splitViewItems = (
    view: GenerationBasisView,
): { cited: GenerationBasisViewItem[]; others: GenerationBasisViewItem[] } => {
    const cited = new Set(view.citedKeys ?? [])
    return {
        cited: view.items.filter((item) => cited.has(item.key)),
        others: view.items.filter((item) => !cited.has(item.key)),
    }
}
